import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { useMemo } from "react";
import {
	useAbbreviationsCache,
	useEntryFiltersCache,
	useMattersCache,
	useSettingsCache,
} from "@/shared/cache";
import type { AbbreviationsSliceData } from "@/shared/cache/slices/abbreviations";
import type { Matter } from "@/shared/cache/slices/matters";
import { parseSettings, type Settings } from "@/shared/cache/slices/settings";
import { useApi } from "@/shared/utils/request";
import { actingHeaders } from "@/v2/hooks/actions/types";

type EntryFormContextData = {
	settings: Settings;
	matters: Matter[];
	abbreviations: AbbreviationsSliceData;
};

export function useEntryFormContext(timekeeperId?: string | null) {
	const { settings: ownSettings, isLoading: ownSettingsLoading } =
		useSettingsCache();
	const { matters: ownMatters, isLoading: ownMattersLoading } =
		useMattersCache();
	const ownAbbreviations = useAbbreviationsCache();
	const { entryFilters } = useEntryFiltersCache();
	const { user } = useAuth();
	const { makeApiRequest } = useApi();

	// The entry belongs to the current user when no timekeeper is specified, or
	// the specified professional id matches our own. In that case the shared
	// caches (already populated by the entries page) hold the right data, so read
	// them directly — instant — instead of refetching over the network. Any other
	// id is a different timekeeper's data and must come from the scoped fetch.
	const isSelf =
		!timekeeperId || timekeeperId === entryFilters.currentTimekeeperId;

	const query = useQuery({
		queryKey: [
			"entry-form-context",
			user?.id ?? "anonymous",
			timekeeperId ?? "self",
		],
		queryFn: async (): Promise<EntryFormContextData> => {
			const headers = actingHeaders(timekeeperId);
			const [settingsResponse, mattersResponse, abbreviationsResponse] =
				await Promise.all([
					makeApiRequest({ endpoint: "/settings", method: "GET", headers }),
					makeApiRequest({ endpoint: "/matters", method: "GET", headers }),
					makeApiRequest({
						endpoint: "/abbreviations",
						method: "GET",
						headers,
					}),
				]);
			for (const response of [
				settingsResponse,
				mattersResponse,
				abbreviationsResponse,
			]) {
				if (response.status === "ERROR") {
					throw new Error(
						response.error || "Failed to load timekeeper context",
					);
				}
			}
			const abbreviationData = abbreviationsResponse.data ?? {};
			return {
				settings: parseSettings(settingsResponse.data?.[0]),
				matters: (mattersResponse.data ?? []) as Matter[],
				abbreviations: {
					enabled: abbreviationData.enabled ?? true,
					abbreviations: abbreviationData.abbreviations ?? [],
				},
			};
		},
		enabled: Boolean(timekeeperId && user?.id) && !isSelf,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	const abbreviations = isSelf
		? {
				enabled: ownAbbreviations.enabled,
				abbreviations: ownAbbreviations.abbreviations,
			}
		: query.data?.abbreviations;
	const abbreviationMap = useMemo(
		() =>
			new Map(
				(abbreviations?.abbreviations ?? []).map((item) => [
					item.abbreviation,
					item.expansion,
				]),
			),
		[abbreviations?.abbreviations],
	);

	return {
		settings: isSelf ? ownSettings : (query.data?.settings ?? ownSettings),
		matters: isSelf ? ownMatters : (query.data?.matters ?? []),
		abbreviationsEnabled: abbreviations?.enabled ?? true,
		abbreviationMap,
		isLoading: isSelf
			? ownSettingsLoading || ownMattersLoading || ownAbbreviations.isLoading
			: query.isPending || query.data === undefined,
		error: query.error instanceof Error ? query.error.message : null,
	};
}
